<script setup lang="ts">
import { ExclamationTriangleIcon } from '@heroicons/vue/24/solid';
import { useForm } from 'vee-validate';
import { brokerLoanStartSchema } from '@/validation';
import { useBrokerLoanAppProcess } from '@/helpers/useBrokerLoanAppProcess';
import type { LendingProduct } from '@/types';

withDefaults(
  defineProps<{
    product?: LendingProduct | null;
    showStartButton?: boolean;
    showBackButton?: boolean;
  }>(),
  {
    product: null,
    showStartButton: true,
    showBackButton: false,
  },
);

const emit = defineEmits<{ confirm: []; back: [] }>();

const { loanApp, state } = useBrokerLoanAppProcess();

type BrokerLoanStartEntry = {
  complianceCheck?: boolean;
};

const { handleSubmit: validateSubmit } = useForm<BrokerLoanStartEntry>({
  validationSchema: brokerLoanStartSchema,
  initialValues: { complianceCheck: loanApp.value.complianceCheck ?? false },
  validateOnMount: false,
});

/**
 * Returns the display label for the given product, or an empty string when none is set.
 * @param {LendingProduct | null | undefined} product - the selected product
 * @returns {string} display label
 */
const productLabel = (product: LendingProduct | null | undefined): string => {
  switch (product) {
    case 'bridging':
      return 'Bridging Loan';
    case 'erhl':
      return 'Equity Release Home Loan';
    default:
      return 'Bridging Loan';
  }
};

const handleConfirm = validateSubmit(async (formValues) => {
  state.value.context.loanApp = {
    ...state.value.context.loanApp,
    complianceCheck: formValues.complianceCheck,
  };
  emit('confirm');
});
</script>

<template>
  <div class="flex w-full flex-1 flex-col">
    <div v-if="product" class="mb-2 mt-4 text-center text-xl font-medium underline">{{ productLabel(product) }}</div>
    <div class="mb-4 mt-4 flex w-full flex-1 flex-col items-center rounded-lg p-4 pt-0">
      <ExclamationTriangleIcon class="h-10 w-10 text-amber-400" />
      <div class="flex flex-1 flex-col text-left">
        <span class="mt-4">
          By continuing with this application, you confirm that you have completed all necessary aggregator compliance
          requirements, including but not limited to:
        </span>
        <ul class="ml-0 mt-2 list-none sm:ml-8">
          <li><span>Privacy and Consent form signed by client/s.</span></li>
          <li><span>Credit Guide provided to the client/s.</span></li>
          <li>
            <span>Reasonable Enquiries and Responsible Lending Requirements have been met under NCCP.</span>
          </li>
          <li><span>Best Interests Duty requirements have completed.</span></li>
        </ul>
        <div class="mt-8">
          <BxField v-slot="{ field }" name="complianceCheck">
            <BxCheckbox
              id="broker-followed-requirements"
              v-bind="field"
              label="Please tick to confirm that you have met all compliance requirements."
            />
          </BxField>
        </div>
      </div>
    </div>
    <div v-if="showStartButton" class="pt-2">
      <div v-if="showBackButton" class="flex items-center justify-between">
        <BxButton id="broker-back" variant="link" @click="emit('back')">Back</BxButton>
        <BxButton id="broker-start-application" variant="primary" @click="handleConfirm()">Next</BxButton>
      </div>
      <BxButton v-else id="broker-start-application" variant="primary" @click="handleConfirm()">
        Start Application
      </BxButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
ul {
  li {
    @apply mt-2;

    &::before {
      @apply text-bridgit-vibrantGreen;
      @apply mr-[-1.125rem] rounded-full font-black;
      content: '✓';
      padding: 2px 4px;
      font-size: 10px;
      background-color: #eeecec;
    }
  }

  span {
    @apply relative left-8 top-[2px] mr-8 inline-flex;
  }
}
</style>
